import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Search,
  Filter,
  Download,
  RefreshCw,
  Loader2,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Eye,
  CreditCard,
  Archive,
  Users,
  UserCheck,
  UserX,
  BookOpen,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/admin-base.css";
import "../styles/members.css";
import client from "../../src/api/client";

const pageSize = 8;

export default function MembersTable({ initialMembers }) {
  const [query, setQuery] = useState("");
  const [company, setCompany] = useState("");
  const [union, setUnion] = useState("");
  const [gender, setGender] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [idStatus, setIdStatus] = useState("");
  const [memberStatus, setMemberStatus] = useState("Active");
  const [ageMin, setAgeMin] = useState("");
  const [ageMax, setAgeMax] = useState("");
  const [registeredFrom, setRegisteredFrom] = useState("");
  const [registeredTo, setRegisteredTo] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [members, setMembers] = useState(initialMembers ?? []);
  const [summaryTotals, setSummaryTotals] = useState({ total: 0, active: 0, inactive: 0 });
  const [metadata, setMetadata] = useState({
    total: initialMembers?.length ?? 0,
    pageCount: 1,
    filters: { companies: [], unions: [], statuses: [], genders: [], cities: [], provinces: [], idStatuses: [] },
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPlacement, setMenuPlacement] = useState("down");
  const [menuCoords, setMenuCoords] = useState({ top: 0, left: 0 });
  const [refreshTick, setRefreshTick] = useState(0);
  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const buildFilterParams = useCallback(() => ({
    query: query || undefined,
    company: company || undefined,
    union: union || undefined,
    gender: gender || undefined,
    city: city || undefined,
    province: province || undefined,
    idStatus: idStatus || undefined,
    memberStatus: memberStatus || undefined,
    ageMin: ageMin || undefined,
    ageMax: ageMax || undefined,
    registeredFrom: registeredFrom || undefined,
    registeredTo: registeredTo || undefined,
  }), [query, company, union, gender, city, province, idStatus, memberStatus, ageMin, ageMax, registeredFrom, registeredTo]);

  const buildSummaryParams = useCallback(() => ({
    query: query || undefined,
    company: company || undefined,
    union: union || undefined,
    gender: gender || undefined,
    city: city || undefined,
    province: province || undefined,
    idStatus: idStatus || undefined,
    ageMin: ageMin || undefined,
    ageMax: ageMax || undefined,
    registeredFrom: registeredFrom || undefined,
    registeredTo: registeredTo || undefined,
  }), [query, company, union, gender, city, province, idStatus, ageMin, ageMax, registeredFrom, registeredTo]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest?.(".admin-row-actions")) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!openMenuId) return undefined;

    const handleClose = () => setOpenMenuId(null);

    window.addEventListener("scroll", handleClose, true);
    window.addEventListener("resize", handleClose, true);
    const scrollEl = scrollRef.current;
    scrollEl?.addEventListener("scroll", handleClose, true);

    return () => {
      window.removeEventListener("scroll", handleClose, true);
      window.removeEventListener("resize", handleClose, true);
      scrollEl?.removeEventListener("scroll", handleClose, true);
    };
  }, [openMenuId]);

  useEffect(() => {
    if (!openMenuId) return;
    document.documentElement.style.setProperty("--admin-menu-top", `${menuCoords.top}px`);
    document.documentElement.style.setProperty("--admin-menu-left", `${menuCoords.left}px`);
  }, [menuCoords, openMenuId]);

  useEffect(() => {
    let subscribed = true;

    const fetchMembers = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await client.get("/api/admin/members", {
          params: {
            page,
            pageSize,
            ...buildFilterParams(),
          },
        });
        if (!subscribed) return;
        const nextMembers = response.data?.results ?? [];
        const nextMetadata = response.data?.metadata ?? {};
        setMembers(nextMembers);
        setMetadata(nextMetadata);

        const summaryBaseParams = buildSummaryParams();
        try {
          const [allSummary, activeSummary, inactiveSummary] = await Promise.all([
            client.get("/api/admin/members", {
              params: {
                page: 1,
                pageSize: 1,
                ...summaryBaseParams,
              },
            }),
            client.get("/api/admin/members", {
              params: {
                page: 1,
                pageSize: 1,
                ...summaryBaseParams,
                memberStatus: "Active",
              },
            }),
            client.get("/api/admin/members", {
              params: {
                page: 1,
                pageSize: 1,
                ...summaryBaseParams,
                memberStatus: "Inactive",
              },
            }),
          ]);

          if (subscribed) {
            setSummaryTotals({
              total: allSummary.data?.metadata?.total ?? 0,
              active: activeSummary.data?.metadata?.total ?? 0,
              inactive: inactiveSummary.data?.metadata?.total ?? 0,
            });
          }
        } catch {
          // Keep current snapshot totals when summary requests fail.
        }

        const nextPageCount = Math.max(1, nextMetadata.pageCount ?? 1);
        if (page > nextPageCount) {
          setPage(nextPageCount);
        }
      } catch (err) {
        if (!subscribed) return;
        setError(err?.response?.data?.message ?? "Unable to load members.");
        setMembers([]);
      } finally {
        if (subscribed) {
          setLoading(false);
        }
      }
    };

    fetchMembers();
    return () => {
      subscribed = false;
    };
  }, [buildFilterParams, buildSummaryParams, page, refreshTick]);

  const handleRefresh = () => {
    setRefreshTick((value) => value + 1);
  };

  // Apply any query params on initial mount (or when the URL changes)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const companyParam = params.get('company');
    const unionParam = params.get('union');
    const queryParam = params.get('query');
    if (companyParam) {
      setCompany(companyParam);
      setPage(1);
    }
    if (unionParam) {
      setUnion(unionParam);
      setPage(1);
    }
    if (queryParam) {
      setQuery(queryParam);
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const pageCount = Math.max(1, metadata.pageCount ?? 1);
  const totalMembers = metadata.total ?? members.length;
  const showing = members.length;
  const isArchiveView = memberStatus === "Inactive";
  const activeFilterCount = [
    company,
    union,
    gender,
    city,
    province,
    idStatus,
    ageMin,
    ageMax,
    registeredFrom,
    registeredTo,
    memberStatus !== "Active" ? memberStatus : "",
  ].filter(Boolean).length;

  const companies = metadata.filters?.companies ?? [];
  const unions = metadata.filters?.unions ?? [];
  const genders = metadata.filters?.genders ?? [];
  const cities = metadata.filters?.cities ?? [];
  const provinces = metadata.filters?.provinces ?? [];
  const idStatuses = metadata.filters?.idStatuses ?? ["Issued", "Pending Activation", "Not Issued"];

  const handleClearFilters = () => {
    setCompany("");
    setUnion("");
    setGender("");
    setCity("");
    setProvince("");
    setIdStatus("");
    setMemberStatus("Active");
    setAgeMin("");
    setAgeMax("");
    setRegisteredFrom("");
    setRegisteredTo("");
    setPage(1);
  };

  const handleViewProfile = (member) => {
    setOpenMenuId(null);
    navigate(`/admin/members/${member.id}`);
  };

  const handleIssueId = (member) => {
    setOpenMenuId(null);
    navigate(`/admin/id-card-management?member=${encodeURIComponent(member.memberID)}`);
  };

  const toggleMenu = (memberId, event) => {
    const target = event?.currentTarget;
    if (!target) {
      setMenuPlacement("down");
      setOpenMenuId((current) => (current === memberId ? null : memberId));
      return;
    }

    const rect = target.getBoundingClientRect();
    const menuWidth = 190;
    const menuHeight = 120; // approximate height of menu (2 items + padding)
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const fitsBelow = rect.bottom + menuHeight <= viewportHeight - 12;
    const left = Math.max(8, Math.min(rect.right - menuWidth, viewportWidth - menuWidth - 8));
    const topRaw = fitsBelow ? rect.bottom + 8 : rect.top - menuHeight - 8;
    const top = Math.max(8, Math.min(topRaw, viewportHeight - menuHeight - 8));
    const placement = top === rect.bottom + 8 || topRaw === rect.bottom + 8 ? "down" : "up";
    setMenuCoords({ top, left });
    setMenuPlacement(placement);
    setOpenMenuId((current) => (current === memberId ? null : memberId));
  };

  const handleExportCsv = async () => {
    try {
      setExporting(true);
      setError(null);
      const response = await client.get("/api/admin/members/export", {
        params: buildFilterParams(),
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `members-directory-${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.message ?? "Unable to export members.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="admin-page members-table-page">
      <header className="admin-surface members-hero">
        <div className="members-hero__intro">
          <span className="members-hero__eyebrow">Member Operations</span>
          <h1>{isArchiveView ? "Archive - Inactive Members" : "Member Directory"}</h1>
          <p className="admin-muted">
            {isArchiveView
              ? "Members who have not logged in within the configured inactivity threshold."
              : "Review member records, monitor union affiliations, and export the dataset for compliance reporting."}
          </p>
        </div>
        <div className="admin-actions members-hero__actions">
          <button
            type="button"
            className={`admin-icon-button admin-icon-button--refresh ${loading ? "is-loading" : ""}`}
            onClick={handleRefresh}
            disabled={loading}
            aria-label="Refresh members"
            title="Refresh members"
          >
            <RefreshCw size={18} />
          </button>
          {isArchiveView ? (
            <button
              type="button"
              className="admin-button is-primary"
              onClick={() => { setMemberStatus("Active"); setPage(1); }}
            >
              Back to Active Members
            </button>
          ) : (
            <button
              type="button"
              className="admin-button"
              onClick={() => { setMemberStatus("Inactive"); setPage(1); }}
            >
              <Archive size={16} /> Archive
            </button>
          )}
          <button
            type="button"
            className="admin-button is-primary"
            onClick={handleExportCsv}
            disabled={exporting}
          >
            {exporting ? <Loader2 size={16} className="admin-spin" /> : <Download size={16} />} {exporting ? "Exporting..." : "Export CSV"}
          </button>
        </div>
      </header>

        <div className="admin-card-grid members-summary-grid">
          <article className="admin-card members-summary-card">
            <p className="admin-card__label">Total Members (Active + Inactive)</p>
            <div className="admin-row">
              <p className="admin-card__value">{summaryTotals.total}</p>
              <span className="admin-chip is-blue"><Users size={18} /></span>
            </div>
            <p className="admin-card__meta">Combined member count based on current filters.</p>
          </article>
          <article className="admin-card members-summary-card">
            <p className="admin-card__label">Total Active Members</p>
            <div className="admin-row">
              <p className="admin-card__value">{summaryTotals.active}</p>
              <span className="admin-chip is-green"><UserCheck size={18} /></span>
            </div>
            <p className="admin-card__meta">Members active within the current filter scope.</p>
          </article>
          <article className="admin-card members-summary-card">
            <p className="admin-card__label">Total Inactive Members</p>
            <div className="admin-row">
              <p className="admin-card__value">{summaryTotals.inactive}</p>
              <span className="admin-chip is-orange"><UserX size={18} /></span>
            </div>
            <p className="admin-card__meta">Members inactive within the current filter scope.</p>
          </article>
          <article className="admin-card members-summary-card">
            <p className="admin-card__label">Current Page</p>
            <div className="admin-row">
              <p className="admin-card__value">{page} / {pageCount}</p>
              <span className="admin-chip is-purple"><BookOpen size={18} /></span>
            </div>
            <p className="admin-card__meta">Navigate across member results.</p>
          </article>
        </div>

      <section className="admin-surface members-section members-section--controls">
        <div className="admin-row members-table__search-row">
          <label className="members-table__search-field">
            <span className="admin-card__label">Search Members</span>
            <div className="admin-card members-table__search-card">
              <Search size={18} color="#64748b" />
              <input
                type="text"
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setPage(1);
                }}
                placeholder="Search by name, member ID, or email"
                className="members-table__search-input"
              />
            </div>
          </label>

          <div className="id-card-queue__filter-button-wrap members-table__filter-button-wrap">
            <button
              type="button"
              className={`admin-button admin-button--ghost members-table__filter-button ${showFilters ? "is-open" : ""} ${activeFilterCount > 0 ? "is-active" : ""}`}
              onClick={() => setShowFilters((open) => !open)}
              aria-pressed={showFilters}
            >
              <Filter size={18} /> Filters
              {activeFilterCount > 0 ? (
                <span className="members-table__filter-badge">{activeFilterCount}</span>
              ) : null}
            </button>
          </div>
        </div>

        {showFilters ? (
          <div className="admin-card admin-filters-panel">
            <div className="admin-filters-panel__header">
              <div>
                <p className="admin-card__label">Refine results</p>
                <p className="admin-filters-panel__hint">Use these filters to narrow the member directory by demographics, status, and registration.</p>
              </div>
              <div className="admin-filters-panel__actions">
                <button type="button" className="admin-button admin-button--secondary" onClick={handleClearFilters}>
                  Clear filters
                </button>
              </div>
            </div>

            <div className="admin-filters-panel__grid">
              <label className="admin-field">
                <span className="admin-card__label">Age From</span>
                <input type="number" value={ageMin} onChange={(e) => { setAgeMin(e.target.value); setPage(1); }} placeholder="Min" />
              </label>
              <label className="admin-field">
                <span className="admin-card__label">Age To</span>
                <input type="number" value={ageMax} onChange={(e) => { setAgeMax(e.target.value); setPage(1); }} placeholder="Max" />
              </label>
              <label className="admin-field">
                <span className="admin-card__label">Gender</span>
                <select value={gender} onChange={(e) => { setGender(e.target.value); setPage(1); }} className="admin-select">
                  <option value="">All</option>
                  {genders.map((item) => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </select>
              </label>
              <label className="admin-field">
                <span className="admin-card__label">City</span>
                <select value={city} onChange={(e) => { setCity(e.target.value); setPage(1); }} className="admin-select">
                  <option value="">All</option>
                  {cities.map((item) => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </select>
              </label>
              <label className="admin-field">
                <span className="admin-card__label">Province</span>
                <select value={province} onChange={(e) => { setProvince(e.target.value); setPage(1); }} className="admin-select">
                  <option value="">All</option>
                  {provinces.map((item) => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </select>
              </label>
              <label className="admin-field">
                <span className="admin-card__label">ID Status</span>
                <select value={idStatus} onChange={(e) => { setIdStatus(e.target.value); setPage(1); }} className="admin-select">
                  <option value="">All</option>
                  {idStatuses.map((item) => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </select>
              </label>
              <label className="admin-field">
                <span className="admin-card__label">Member Status</span>
                <select value={memberStatus} onChange={(e) => { setMemberStatus(e.target.value); setPage(1); }} className="admin-select">
                  <option value="">All</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </label>
              <label className="admin-field">
                <span className="admin-card__label">Registered From</span>
                <input type="date" value={registeredFrom} onChange={(e) => { setRegisteredFrom(e.target.value); setPage(1); }} />
              </label>
              <label className="admin-field">
                <span className="admin-card__label">Registered To</span>
                <input type="date" value={registeredTo} onChange={(e) => { setRegisteredTo(e.target.value); setPage(1); }} />
              </label>
            </div>
          </div>
        ) : null}

        <div className="admin-row admin-row--filters-inline">
          <select
            className="admin-select"
            value={company}
            onChange={(event) => {
              setCompany(event.target.value);
              setPage(1);
            }}
          >
            <option value="">Company (All)</option>
            {companies.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>

          <select
            className="admin-select"
            value={union}
            onChange={(event) => {
              setUnion(event.target.value);
              setPage(1);
            }}
          >
            <option value="">Union (All)</option>
            {unions.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>

          <select
            className="admin-select"
            value={memberStatus}
            onChange={(event) => {
              setMemberStatus(event.target.value);
              setPage(1);
            }}
          >
            <option value="">Status (All)</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>

        </div>
      </section>

      <section className="admin-surface members-section members-section--table">
        <div className="admin-row">
          <h2>Members ({totalMembers})</h2>
          <div className="members-table__head-pills">
            <span className="admin-pill">Showing {showing} records</span>
            <span className="admin-pill">View {memberStatus}</span>
          </div>
        </div>

        {error ? (
          <div className="admin-empty-state">{error}</div>
        ) : null}

        <div className="admin-scroll" ref={scrollRef}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Member ID</th>
                <th>Name</th>
                <th>Company</th>
                <th>Union</th>
                <th>ID Card</th>
                <th>Registered</th>
                <th>Status</th>
                <th className="members-table__actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.id}>
                  <td className="members-table__member-id">{member.memberID}</td>
                  <td>
                    <div className="members-table__name-row">
                      <div>
                        <div className="members-table__member-name">{member.fullName}</div>
                        <div className="admin-muted">{member.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="admin-stat">
                      <span>Company</span>
                      <span>{member.company}</span>
                    </div>
                  </td>
                  <td>
                    <div className="admin-stat">
                      <span>Union</span>
                      <span>{member.unionAffiliation}</span>
                    </div>
                  </td>
                  <td>{member.idStatus}</td>
                  <td>
                    {member.registeredDate
                      ? new Date(member.registeredDate).toLocaleDateString()
                      : "—"}
                  </td>
                  <td>
                    <span className={`admin-chip ${member.status === "Active" ? "is-green" : member.status === "Inactive" ? "is-orange" : "is-blue"}`}>
                      {member.status}
                    </span>
                  </td>
                  <td>
                    <div className="admin-row-actions">
                      <button
                        type="button"
                        className="admin-icon-button"
                        onClick={(event) => toggleMenu(member.id, event)}
                        aria-label="Member actions"
                      >
                        <MoreHorizontal size={16} />
                      </button>
                      {openMenuId === member.id ? (
                        <div
                          className={`admin-menu admin-menu--fixed ${menuPlacement === "up" ? "admin-menu--top" : ""}`}
                        >
                          <button type="button" onClick={() => handleViewProfile(member)}>
                            <Eye size={14} />
                            View profile
                          </button>
                          <button type="button" onClick={() => handleIssueId(member)}>
                            <CreditCard size={14} />
                            Issue ID
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && members.length === 0 && (
                <tr>
                  <td colSpan={8}>
                    <div className="admin-empty-state">
                      No members matched your filters. Adjust your search and try
                      again.
                    </div>
                  </td>
                </tr>
              )}
              {loading ? (
                <tr>
                  <td colSpan={8}>
                    <div className="admin-empty-state">Loading members…</div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <footer className="admin-row members-table__footer">
          <span className="admin-muted">
            Page {page} of {pageCount}
          </span>
          <span className="admin-pill is-blue">
            Showing {members.length} of {Number(metadata?.total ?? members.length)}
          </span>
          <div className="admin-actions">
            <button
              type="button"
              className="admin-button members-table__pager-button"
              onClick={() => setPage((value) => Math.max(1, value - 1))}
              disabled={page === 1}
            >
              <ChevronLeft size={16} /> Previous
            </button>
            <button
              type="button"
              className="admin-button members-table__pager-button"
              onClick={() => setPage((value) => Math.min(pageCount, value + 1))}
              disabled={page === pageCount}
            >
              Next <ChevronRight size={16} />
            </button>
          </div>
        </footer>
      </section>
    </div>
  );
}

MembersTable.propTypes = {
  initialMembers: PropTypes.arrayOf(PropTypes.object),
};
